import { ComponentType, lazy, LazyExoticComponent } from 'react';

// After a deploy the previous build's hashed chunks are gone, so a tab that
// was opened before it fails to load the next lazy route. Reloading once pulls
// the new index.html (and its new chunk names) instead of showing the error
// page. The timestamp guard stops a reload loop when the chunk is really
// unreachable (e.g. offline) — the error then reaches the ErrorBoundary.
const RELOAD_KEY = 'aux:chunk-reload';
const RELOAD_WINDOW_MS = 10_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

export const isChunkLoadError = (error: unknown) =>
  error instanceof Error && CHUNK_ERROR.test(error.message);

/** Reloads the page unless it already did so in the last few seconds. */
export const reloadOnce = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY));
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: still reload — worst case is the error page after.
  }
  window.location.reload();
  return true;
};

const clearReloadFlag = () => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore
  }
};

export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> =>
  lazy(() =>
    factory().then(
      (module) => {
        clearReloadFlag();
        return module;
      },
      (error) => {
        if (isChunkLoadError(error) && reloadOnce()) {
          // Keep the Suspense fallback up until the reload takes over.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }
    )
  );
